/**
 * What to check in text the model just wrote, shown right after a
 * generate or Generate everything:
 *
 *  - NB-17: a sentence stating a test result or "no detectable
 *    difference" the disclosure does not contain — or one that
 *    contradicts it (the tea letter says speed-wilted tea is of lower
 *    quality; the draft said there was no detectable difference);
 *  - NB-29: a sentence citing a figure for a reference numeral that
 *    figure does not show ("As shown in FIG. 2, the compressor 210");
 *  - NB-36: a sentence calling a registered reference numeral by
 *    another part's name ("the rolling 230" where 230 is the
 *    steaming chamber);
 *  - NB-28: a paragraph kept with a problem, or a claim family that
 *    wrote nothing, instead of the whole section failing.
 *
 * Each entry names its paragraph. Advisory: the text is on the page as
 * written, and section Verify flags the same sentences.
 */
import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';

function where(n?: string | null): string {
  return n ? `[${n}] ` : '';
}

export default function GenerationFindingsBanner({
  sectionId,
}: {
  sectionId: string;
}): JSX.Element | null {
  const entry = useSpecStore((s) => s.findingWarnings[sectionId]);
  const dismiss = useSpecStore((s) => s.dismissFindingWarning);
  const section = useSpecStore(
    (s) => s.sections.find((sec) => sec.id === sectionId) || null,
  );

  if (!entry) return null;
  if (section?.is_frozen) return null;
  const unsupported = entry.unsupported_statements || [];
  const figures = entry.figure_mismatches || [];
  const renamed = entry.numeral_label_mismatches || [];
  const kept = entry.validation_warnings || [];
  if (unsupported.length + figures.length + renamed.length + kept.length === 0) {
    return null;
  }

  return (
    <div
      className="mt-3 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-zinc-800"
      role="alert"
      data-testid={`spec-findings-warning-${sectionId}`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
        <div className="flex-1 space-y-2">
          <div className="font-medium text-amber-900">Check the new text</div>
          {unsupported.length > 0 && (
            <div data-testid={`spec-findings-unsupported-${sectionId}`}>
              <div className="text-zinc-700">
                {'Not in the disclosure — remove or reword unless the inventor supplies it:'}
              </div>
              <ul className="ml-2 list-disc pl-3 text-zinc-600">
                {unsupported.map((u, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i}>
                    {`${where(u.paragraph_number)}“${u.sentence}”`}
                    {u.contradicts && u.disclosure_sentence
                      ? ` — contradicts the disclosure, which says: “${u.disclosure_sentence}”`
                      : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {figures.length > 0 && (
            <div data-testid={`spec-findings-figures-${sectionId}`}>
              <div className="text-zinc-700">
                {'A figure is cited for an element it does not show:'}
              </div>
              <ul className="ml-2 list-disc pl-3 text-zinc-600">
                {figures.map((f, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i}>
                    {`${where(f.paragraph_number)}${[f.label, f.numeral].filter(Boolean).join(' ')} is not on ${f.figures.join(' or ')}${
                      f.on_figures.length > 0 ? ` (it is on ${f.on_figures.join(', ')})` : ''
                    }: “${f.sentence}”`}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {renamed.length > 0 && (
            <div data-testid={`spec-findings-renamed-${sectionId}`}>
              <div className="text-zinc-700">
                {'A reference numeral is called by another part’s name — use the registered name, or the right numeral:'}
              </div>
              <ul className="ml-2 list-disc pl-3 text-zinc-600">
                {renamed.map((f, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i}>
                    {`${where(f.paragraph_number)}${f.numeral} is registered as “${f.label}” but is called “${f.called}” here: “${f.sentence}”`}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {kept.length > 0 && (
            <div data-testid={`spec-findings-kept-${sectionId}`}>
              <div className="text-zinc-700">
                {'Kept with a problem instead of failing the section:'}
              </div>
              <ul className="ml-2 list-disc pl-3 text-zinc-600">
                {kept.map((w, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i}>{`${where(w.paragraph_number)}${w.detail}`}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <button
          type="button"
          className="rounded-sm p-1 text-zinc-400 hover:bg-amber-100 hover:text-zinc-700"
          onClick={() => dismiss(sectionId)}
          aria-label="Dismiss findings for the new text"
          data-testid={`spec-findings-warning-dismiss-${sectionId}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
